import React from 'react';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import SettingField from 'components/Fields/ReduxForm/SettingField';
import ConfirmModal from 'components/ConfirmModal';
import {
  onClickCancel,
  deleteCategoryRequest,
  showUpdateModal,
  hideUpdateModal,
} from 'actions/library/category';
import withFeatureFlags from 'hocs/withFeatureFlags';
import { isDisableOutletLevelForm } from 'utils/userProperties';

import RewardAlert from '../../RewardAlert';
import styles from './style.css';

const validate = values => {
  const errors = {};
  if (!values.name || values.name.trim() === '') {
    errors.name = 'Please enter a value';
  }
  return errors;
};

const tooltipDeleteButton = (
  <Popover id="popover-trigger-hover-focus">
    This category cannot be deleted. Go to Business Library to delete
  </Popover>
);

const tooltipSaveButton = (
  <Popover id="popover-trigger-hover-focus">
    This category cannot be edited. Go to Business Library to modify
  </Popover>
);

class CategoryForm extends React.Component {
  render() {
    const {
      handleSubmit,
      isSubmittingForm,
      showUpdateModal,
      categoryDetail,
      businessLibraryMenu,
    } = this.props;

    const isDisableForm = isDisableOutletLevelForm(
      businessLibraryMenu,
      categoryDetail
    );

    return (
      <form
        className={styles.wrapper}
        onSubmit={e => {
          e.preventDefault();
        }}
      >
        <div className={`${styles['field-wrapper']} category-field-wrapper`}>
          <div className={styles.title}>Category</div>
          <div className="row">
            <div className="col-sm-12">
              <Field
                component={SettingField}
                name="name"
                className="form-control"
                placeholder="Category Name"
                dis={isDisableForm}
              />
            </div>
          </div>
          <div className={`row ${styles['margin-top-10']}`}>
            {categoryDetail &&
              (categoryDetail.is_reward_category ||
                categoryDetail.is_point_gain_category) && (
                <div className="col-lg-12">
                  <RewardAlert
                    title="Loyalty Program Category"
                    content="Any changes you make to this category will affect your loyalty program"
                    type="info"
                  />
                </div>
              )}
          </div>
        </div>
        <div className={styles.footer}>
          {this.props.category.id ? (
            isDisableForm ? (
              <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                overlay={tooltipDeleteButton}
              >
                <button
                  className={`btn ${styles['disabled-button']}`}
                  onClick={e => e.preventDefault()}
                >
                  <i className="fa fa-trash" />
                </button>
              </OverlayTrigger>
            ) : (
              <span className="pull-left">
                <button
                  className="btn btn-danger"
                  onClick={this._onClickDelete}
                  type="button"
                  disabled={isSubmittingForm}
                >
                  <i className="fa fa-trash" />
                </button>
              </span>
            )
          ) : null}
          <span className="pull-right">
            <button
              className={`btn btn-default ${styles['btn-cancel']}`}
              type="button"
              onClick={this._onClickCancel}
              disabled={isSubmittingForm}
            >
              Cancel
            </button>
            {!this.props.category.id && this.props.outlets.length > 1 && (
              <button
                className={`btn btn-primary ${styles['btn-save']}`}
                type="button"
                onClick={handleSubmit(this._onSubmitToAllOutlet)}
                disabled={isSubmittingForm}
              >
                Save to All Outlets
              </button>
            )}
            {isDisableForm ? (
              <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                overlay={tooltipSaveButton}
              >
                <button
                  className={`btn ${styles['disabled-button']}`}
                  type="button"
                  onClick={e => e.preventDefault()}
                >
                  Save
                </button>
              </OverlayTrigger>
            ) : (
              <button
                className={`btn btn-primary ${styles['btn-save']}`}
                type="button"
                onClick={handleSubmit(this._onClickSave)}
                disabled={isSubmittingForm}
              >
                Save
              </button>
            )}
          </span>
        </div>
        {this.props.categoryDetail &&
          (this.props.categoryDetail.is_reward_category ||
            this.props.categoryDetail.is_point_gain_category) && (
            <ConfirmModal
              name={this.props.categoryDetail.name}
              onHide={this._onHideUpdateModal}
              showModal={showUpdateModal}
              title="Edit Loyalty Program Category"
              description="You can’t use this category in your loyalty program if you change the category names. Are you sure you still want to edit?"
              onConfirm={handleSubmit(this._onSubmit)}
              baseColor="danger"
              cancelTitle="No"
              confirmTitle="Yes"
            />
          )}
      </form>
    );
  }

  _onClickSave = categoryDetail => {
    if (
      this.props.categoryDetail &&
      (this.props.categoryDetail.is_reward_category ||
        this.props.categoryDetail.is_point_gain_category) &&
      this.props.categoryDetail.name !== categoryDetail.name
    ) {
      const { dispatch } = this.props;
      dispatch(showUpdateModal());
    } else {
      this._onSubmit(categoryDetail);
    }
  };

  _onHideUpdateModal = () => {
    const { dispatch } = this.props;
    dispatch(hideUpdateModal());
  };

  _onSubmit = values => {
    this.props.onSubmit({ ...values, action: 'save' });
    this._onHideUpdateModal();
  };

  _onSubmitToAllOutlet = values => {
    this.props.onSubmit({ ...values, action: 'saveToAllOutlet' });
  };

  _onClickCancel = event => {
    this.props.dispatch(onClickCancel());
    event.preventDefault();
    event.stopPropagation();
  };

  _onClickDelete = event => {
    const { dispatch, categoryDetail } = this.props;
    dispatch(deleteCategoryRequest(categoryDetail));
    event.preventDefault();
    event.stopPropagation();
  };
}

const selector = formValueSelector('category');

const mapForm = {
  form: 'category',
  touchOnBlur: false,
  touchOnChange: true,
  validate,
};

function mapStateToProps(state) {
  const {
    categories,
    categoryDetail,
    outlets,
    isSubmittingForm,
    showUpdateModal,
  } = state.library.category;
  return {
    categories,
    categoryDetail,
    showUpdateModal,
    category: { id: selector(state, 'id') },
    data: {
      form: state.form,
    },
    outlets,
    isSubmittingForm,
  };
}
const connected = connect(mapStateToProps)(reduxForm(mapForm)(CategoryForm));

const mapFlagsToProps = flags => ({
  businessLibraryMenu: flags.businessLibraryMenu,
});

export default withFeatureFlags(mapFlagsToProps)(connected);
